import React, { memo, useEffect, useState } from 'react'
import { Autocomplete } from 'components/forms/autocomplete/Autocomplete'
import PropTypes from 'prop-types'
import { Button } from 'transfix-ui/components/Button'
import { CheckboxDropdown } from 'transfix-ui/components/CheckboxDropdown'
import { Select } from 'transfix-ui/components/Select'
import SuggestLocation from 'transfix-ui/components/SuggestLocation'
import { makeStyles } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { getUserDetails } from 'utils/hooks/useUserDetails'
import { BOOKING_FILTERS } from 'apps/booking/constants/bookingFilters'
import { TODAY_STATS, TOMORROW_STATS } from 'apps/booking/constants/shipments'
import { formatPickupDeliveryDates, clearCheckboxDropdown } from 'apps/booking/utils/filters'
import { defaultState } from 'apps/booking/utils/reducers/filters'
import { today, tomorrow } from 'apps/booking/utils/searchParams'
import VendorAutocomplete from 'apps/marketplace/components/VendorAutocomplete/VendorAutocomplete'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { Calendar, Checkbox } from 'components/forms'
import { capitalize } from 'utils/capitalize'
import { updateCheckboxFilters } from 'utils/updateCheckboxFilters'
import { BROKER_POD_OPTIONS_LEGACY, BROKER_POD_OPTIONS_NEW } from 'constants/brokerPods'
import { BOOKING_REGIONS } from 'constants/countriesStates'
import { EQUIPMENT } from 'constants/equipment'
import { RATE_TYPES } from 'constants/rateTypes'
import { SHIPMENT_STATUSES } from 'constants/shipments'
import { SHIPMENT_TABS } from '../../constants/shipments'
import { useAmPodsQuery } from './graphql/AmPods'

export const SEARCH_TYPE_OPTIONS = [
  { key: 'carrier', value: 'carrier', label: 'Carrier' },
  { key: 'shipper', value: 'shipper', label: 'Company' },
  { key: 'parents', value: 'parents', label: 'User' },
  { key: 'vendor', value: 'vendor', label: 'Vendor' },
]

const useStyles = makeStyles(theme => ({
  searchTypeContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  searchTypeSelect: {
    width: '40%',
    marginRight: theme.spacing(1),
    '& .MuiSelect-select.MuiSelect-outlined': {
      lineHeight: 'normal',
    },
  },
  searchTypeInput: {
    width: '60%',
    '& .MuiAutocomplete-inputRoot': {
      paddingLeft: '1px !important',
    },
    '& div.MuiAutocomplete-inputRoot input.MuiAutocomplete-input:first-child': {
      padding: '4px 6px 4px 27px',
    },
  },
}))

export const BookingFilters = memo(props => {
  const classes = useStyles()
  const { trackEvent } = useMPAnalytics()
  const [searchType, setSearchType] = useState('shipper')
  const isSearchingVendor = searchType === 'vendor'
  const [vendorSearch, setVendorSearch] = useState('')

  const handleFilterChange = (e, type, autoCompleteSuggestion) => {
    const { id: currentUserId } = getUserDetails() || {}
    const {
      filters: { checkboxFilters: otherFilters, podIds },
      filtersUpdate,
      selectedTabIndex,
    } = props
    const { status } = SHIPMENT_TABS[selectedTabIndex]
    filtersUpdate({ status, submitFilters: false })
    let newValue
    if ([BOOKING_FILTERS.pickupDates, BOOKING_FILTERS.deliveryDates].includes(type)) {
      newValue = formatPickupDeliveryDates(e.date)
      return filtersUpdate({ debounce: true, [type]: newValue })
    }

    if ([BOOKING_FILTERS.pickupLocation, BOOKING_FILTERS.deliveryLocation].includes(type)) {
      newValue = e
      return filtersUpdate({ debounce: true, [type]: newValue })
    }

    if ([BOOKING_FILTERS.pickupRegion, BOOKING_FILTERS.deliveryRegion].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.query].includes(type)) {
      newValue = e.target.value
      const submitFiltersValue = newValue.length >= 4
      return filtersUpdate({ submitFilters: submitFiltersValue, debounce: true, [type]: newValue })
    } else if ([BOOKING_FILTERS.radius].includes(type)) {
      newValue = e.target.value
      return filtersUpdate({ debounce: true, [type]: newValue })
    } else if ([BOOKING_FILTERS.stateOptions].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.equipment].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.shipperRateType].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.pricingType].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.brokerPodKey].includes(type)) {
      newValue = e.target.value
    } else if ([BOOKING_FILTERS.podIds].includes(type)) {
      newValue = podIds.map(pod => updateCheckboxFilters(e, pod))
      trackEvent('Booking', 'FILTERED_BY_POD', { Pod: newValue })
    } else if ([BOOKING_FILTERS.myLoads].includes(type)) {
      newValue = e.target.checked
      trackEvent('Booking', 'FILTERED_BY_ME')
      return filtersUpdate({ myLoads: newValue, currentUserId })
    } else if ([BOOKING_FILTERS.myIssues].includes(type)) {
      newValue = e.target.checked
      trackEvent('Booking', 'FILTERED_OWN_ISSUES')
      return filtersUpdate({ myIssues: newValue, currentUserId })
    } else if ([BOOKING_FILTERS.company].includes(type)) {
      newValue = autoCompleteSuggestion
    } else if ([BOOKING_FILTERS.checkboxFilters].includes(type)) {
      newValue = otherFilters.map(filter => updateCheckboxFilters(e, filter))
    } else if ([BOOKING_FILTERS.refresh].includes(type)) {
      return filtersUpdate({ refresh: true })
    } else if (e.target && e.target.type === 'checkbox') {
      newValue = e.target.checked
    } else {
      newValue = e
    }

    return filtersUpdate({ [type]: newValue })
  }

  const clearSearch = () => {
    const {
      filters: { checkboxFilters, podIds },
      filtersUpdate,
      getStatsCount: handlePodChange,
      selectedTabIndex,
    } = props
    const checkboxFilterValues = clearCheckboxDropdown(checkboxFilters)
    const checkboxPodValues = clearCheckboxDropdown(podIds)
    const clearSelectedPodFilters = { podIds: checkboxPodValues }
    const clearSelectedCheckboxFilters = { checkboxFilters: checkboxFilterValues }
    const clearUserParam = { usersId: '', usersType: '' }
    handlePodChange(
      { podIds: [], ...clearUserParam, pickupDate: today },
      { selectedTabIndex, stats: TODAY_STATS }
    )
    handlePodChange(
      { podIds: [], ...clearUserParam, pickupDate: tomorrow },
      { selectedTabIndex, stats: TOMORROW_STATS }
    )
    const { status } = SHIPMENT_TABS[selectedTabIndex]
    filtersUpdate({
      ...defaultState,
      ...clearSelectedCheckboxFilters,
      ...clearSelectedPodFilters,
      [BOOKING_FILTERS.brokerPodKey]: '',
      status,
    })
    trackEvent('Booking', 'CLEARED_SEARCH')
  }

  const showDateRange = rangeType => {
    const { filtersUpdate } = props
    filtersUpdate({ submitFilters: false, [rangeType]: true })
  }

  const preventDefault = e => {
    if (e) e.preventDefault()
  }

  const {
    filters: {
      brokerPodKey,
      checkboxFilters,
      company,
      deliveryDateRange,
      deliveryDates,
      deliveryLocation,
      deliveryRegion,
      equipment,
      myIssues,
      myLoads,
      pickupDateRange,
      pickupDates,
      pickupLocation,
      pickupRegion,
      podIds,
      podIdsNew,
      pricingType,
      query,
      radius,
      shipperRateType,
      stateOptions,
    },
  } = props
  const isBrokerPodsEnabled = useFeatureFlag('useBrokerPods')
  const isNewPodNamesEnabled = useFeatureFlag('mpNewPodNames')
  const isBrokerPodUpdateEnabled = useFeatureFlag('fmsUpdatedBrokerPodOptions')

  const { data } = useAmPodsQuery({
    skip: !isNewPodNamesEnabled,
  })
  const { amPods: result } = data || {}
  const { nodes = [] } = result || {}
  const [amPods, setAmPods] = useState([])

  const BROKER_POD_OPTIONS = isBrokerPodUpdateEnabled
    ? BROKER_POD_OPTIONS_NEW
    : BROKER_POD_OPTIONS_LEGACY

  /**
   * This useEffect should update the pods if the data from the api exists
   */
  useEffect(() => {
    if (nodes.length > 0) {
      setAmPods(
        nodes.map(({ id, name }) => ({
          selected: false,
          text: name,
          value: id,
        }))
      )
    }
  }, [nodes])

  const handleSelectedPodChange = e => {
    const { filtersUpdate } = props
    const newPods = amPods.map(pod => ({
      ...pod,
      selected: pod.value === e.value ? !pod.selected : pod.selected,
    }))
    setAmPods(newPods)
    filtersUpdate({ [BOOKING_FILTERS.podIds]: newPods })
    trackEvent('Booking', 'FILTERED_BY_POD', { Pod: newPods })
  }

  let searchTypePlaceholder = searchType
  if (searchType === 'parents') searchTypePlaceholder = 'User'

  return (
    <form onSubmit={preventDefault} className='mb2'>
      <div className='row flex-l items-center'>
        <div className='col-md-2'>
          <CheckboxDropdown
            dropdownId='pods'
            options={isNewPodNamesEnabled ? amPods : podIds}
            defaultText='All Pods'
            handleChange={v =>
              isNewPodNamesEnabled
                ? handleSelectedPodChange(v)
                : handleFilterChange(v, BOOKING_FILTERS.podIds)
            }
          />
        </div>

        <div className='col-md-2'>
          <select
            value={pickupRegion}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.pickupRegion)}>
            <option value=''>All Pickup Regions</option>
            {BOOKING_REGIONS.map(e => (
              <option key={e.name} value={e.abbr}>
                {e.abbr}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-2'>
          <select
            value={deliveryRegion}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.deliveryRegion)}>
            <option value=''>All Delivery Regions</option>
            {BOOKING_REGIONS.map(e => (
              <option key={e.name} value={e.abbr}>
                {e.abbr}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-2'>
          <select
            value={shipperRateType}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.shipperRateType)}
            data-test='shipper-rate-type-filter'>
            <option value=''>All Rate Types</option>
            {RATE_TYPES.map(e => (
              <option key={e.name} value={e.abbr}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-2'>
          <select
            value={pricingType}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.pricingType)}
            data-test='pricing-type-filter'>
            <option value=''>All Pricing Types</option>
            <option value='fixed' key='fixed'>
              Fixed
            </option>
            <option value='variable' key='variable'>
              Variable - TrueRate+
            </option>
          </select>
        </div>
        {isBrokerPodsEnabled && (
          <div className='col-md-2'>
            <select
              value={brokerPodKey}
              onChange={v => handleFilterChange(v, BOOKING_FILTERS.brokerPodKey)}>
              <option value=''>All Broker Pods</option>
              {BROKER_POD_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className='col-md-2 nowrap'>
          <div className='flex'>
            <Checkbox
              id='myLoads'
              dataTest='my-loads-toggle'
              label='Assigned to Me'
              checked={myLoads}
              onChange={v => handleFilterChange(v, BOOKING_FILTERS.myLoads)}
            />
          </div>
        </div>
        <div className='col-md-2 nowrap'>
          <div className='flex'>
            <Checkbox
              id='myIssues'
              dataTest='my-issues-toggle'
              label='Issues I Own'
              checked={myIssues}
              onChange={v => handleFilterChange(v, BOOKING_FILTERS.myIssues)}
            />
          </div>
        </div>
        <div className='col-md-1 md-shift-2'>
          <Button
            type='button'
            data-test='clear-filters-button'
            onClick={clearSearch}
            fullWidth
            variant='text'>
            Clear All
          </Button>
        </div>
        <div className='col-md-1'>
          <button
            onClick={v => handleFilterChange(v, BOOKING_FILTERS.refresh)}
            data-test='refresh-button'
            className='btn-full-width btn-primary'>
            Refresh
          </button>
        </div>
      </div>
      <hr className='mt2 mb4' />
      <div className='row flex-l items-center'>
        <div className='col-md-3'>
          <SuggestLocation
            placeholder='Pickup City or State'
            value={pickupLocation}
            resultTypes={['City', 'State', 'PostalCode']}
            dataTest={BOOKING_FILTERS.pickupLocation}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.pickupLocation)}
          />
        </div>
        <div className={pickupDateRange ? 'col-md-3' : 'col-md-2'}>
          <Calendar
            dataTest='pickup-date-input'
            size='small'
            placeholder={pickupDateRange ? 'Start Date - End Date' : 'Date'}
            acceptMultiple
            options={
              pickupDateRange
                ? {
                    mode: 'range',
                    showMonths: 2,
                  }
                : {
                    mode: 'single',
                    showMonths: 1,
                  }
            }
            date={pickupDates}
            dateTimeChangeToState={v => handleFilterChange(v, BOOKING_FILTERS.pickupDates)}
          />
        </div>
        {!pickupDateRange && (
          <div className='col-md-1 mt1'>
            <button
              data-test='pickup-range-button'
              type='button'
              onClick={() => showDateRange('pickupDateRange')}
              className='btn-text-link'>
              + Date Range
            </button>
          </div>
        )}
        <div className='col-md-3'>
          <SuggestLocation
            placeholder='Delivery City or State'
            resultTypes={['City', 'State', 'PostalCode']}
            value={deliveryLocation}
            dataTest={BOOKING_FILTERS.deliveryLocation}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.deliveryLocation)}
          />
        </div>
        <div className={deliveryDateRange ? 'col-md-3' : 'col-md-2'}>
          <Calendar
            dataTest='delivery-date-input'
            size='small'
            placeholder={deliveryDateRange ? 'Start Date - End Date' : 'Date'}
            acceptMultiple
            options={
              deliveryDateRange
                ? {
                    mode: 'range',
                    showMonths: 2,
                  }
                : {
                    mode: 'single',
                    showMonths: 1,
                  }
            }
            date={deliveryDates}
            dateTimeChangeToState={v => handleFilterChange(v, BOOKING_FILTERS.deliveryDates)}
          />
        </div>
        {!deliveryDateRange && (
          <div className='col-md-1 mt1'>
            <button
              data-test='delivery-range-button'
              type='button'
              onClick={() => showDateRange('deliveryDateRange')}
              className='btn-text-link'>
              + Date Range
            </button>
          </div>
        )}
      </div>
      <div className='row flex-l items-center mt2'>
        <div className='col-md-2'>
          <select
            data-test='shipment-status-filter'
            value={stateOptions}
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.stateOptions)}>
            <option value=''>Status</option>
            {SHIPMENT_STATUSES.filter(e => !e.hide).map(e => (
              <option data-test={e.value} key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-2'>
          <input
            type='text'
            value={query}
            placeholder='ID, PO, BOL, etc'
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.query)}
          />
        </div>
        <div className={`${classes.searchTypeContainer} col-md-3`}>
          <div className={classes.searchTypeSelect}>
            <Select
              value={searchType}
              options={SEARCH_TYPE_OPTIONS}
              onChange={event => {
                handleFilterChange('', BOOKING_FILTERS.company, undefined)
                setSearchType(event.target.value)
              }}
              placeholder='Select search type'
              dataTest='input-search-type'
            />
          </div>
          <div className={classes.searchTypeInput}>
            {isSearchingVendor && (
              <VendorAutocomplete
                name='name'
                value={vendorSearch}
                AutocompleteProps={{
                  placeholder: 'Search Vendor Name',
                }}
                dataTest='search-vendor-name'
                onChange={(newVal, vendorId) => {
                  setVendorSearch(newVal)
                  if (vendorId) {
                    handleFilterChange(newVal, BOOKING_FILTERS.company, {
                      id: vendorId,
                      type: 'vendor',
                    })
                  } else {
                    handleFilterChange('', BOOKING_FILTERS.company, undefined)
                  }
                }}
              />
            )}
            {!isSearchingVendor && (
              <Autocomplete
                placeholder={capitalize(searchTypePlaceholder)}
                id='company-users-search'
                value={company}
                onChange={({ fullSelection, value }) =>
                  handleFilterChange(value, BOOKING_FILTERS.company, fullSelection)
                }
                carrier='carrier_activated'
                shipper='shipper_confirmed'
                type={searchType}
                renderOrder={['shipper', 'parents', 'carrier']}
              />
            )}
          </div>
        </div>
        <div className='col-md-1'>
          <input
            type='number'
            placeholder='Radius'
            value={radius}
            data-test='radius-filter'
            onChange={v => handleFilterChange(v, BOOKING_FILTERS.radius)}
          />
        </div>
        <div className='col-md-2'>
          <select value={equipment} onChange={v => handleFilterChange(v, 'equipment')}>
            <option value=''>Equipment Type</option>
            {EQUIPMENT.filter(
              e =>
                e.key === 'dry_van' ||
                e.key === 'reefer' ||
                e.key === 'open_deck' ||
                e.key === 'specialized'
            ).map(e => (
              <option key={e.key} value={e.key}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-2'>
          <CheckboxDropdown
            dropdownId='filters'
            defaultText='Other filters'
            dataTest='checkbox-dropdown-filters'
            options={checkboxFilters}
            handleChange={v => handleFilterChange(v, BOOKING_FILTERS.checkboxFilters)}
          />
        </div>
      </div>
    </form>
  )
})

BookingFilters.propTypes = {
  location: PropTypes.shape({}),
  getStatsCount: PropTypes.func,
  params: PropTypes.shape({}),
  selectedTabIndex: PropTypes.number,
  filtersUpdate: PropTypes.func,
  filters: PropTypes.shape({}),
}
BookingFilters.defaultProps = {
  location: {},
  getStatsCount: () => {},
  params: {},
  selectedTabIndex: 2,
  filtersUpdate: () => {},
  filters: {},
}
